import { useState, useEffect } from 'react';
import { isEmpty, round2, focusWidget, deep_copy } from '@/util';
import { FroalaTextareaView } from '@/components/Froala/FroalaTextareaView';
import { useTranslation } from 'react-i18next';
import { WidgetHeader } from './WidgetHeader';
import { encode } from '@/base64';
import { ExplanationSection } from '../ExplanationSection';
import { PartialSubmitSection } from '../PartialSubmitSection';
import { XAPIService } from '@/services/xapi.service';
import GroupedItemsLearnerWidget from './SortGroupsViewContent';

export const SortGroupsView = ({
  state,
  answer,
  correction,
  correctionType = undefined,
  courseStatus = undefined,
  index,
  setAnswerFn = undefined,
  setXApiSubmitFn = undefined,
  setCorrectionFn = undefined,
  onModified = undefined,
  isCorrected,
  onSave = undefined,
  viewOnly,
  resultPages,
  showAnswers = false,
}) => {
  const { t } = useTranslation();

  answer ||= {};
  correction ||= {};

  SortGroupsView.syncStates(state, answer, correction);

  const [submitted, setSubmitted] = useState(!!answer?.submitted);
  const [score, setScore] = useState(correction?.score);
  const answered = submitted || isCorrected;
  const [modified, setModified] = useState(null);

  const [myGroups, setMyGroups] = useState(deep_copy(answer?.groups || []));
  const [bankItems, setBankItems] = useState([]);

  // console.log('answer', answer);
  // console.log('correction', correction);

  useEffect(() => {
    if (submitted) {
      let answer = getAnswer();
      let correction = {};
      SortGroupsView.syncStates(state, answer, correction);
      if (!answer?.answered) {
        setScore(correction?.score);
      }
    }
  }, [submitted]);

  const getAnswer = () => {
    let newAnswer = deep_copy(answer);

    newAnswer.groups = myGroups;
    newAnswer.incomplete = !submitted && bankItems?.length > 0;
    newAnswer.submitted = submitted;
    return newAnswer;
  };
  setAnswerFn && setAnswerFn(getAnswer);

  // generate the correction
  const getCorrection = () => {
    let correction = {};
    SortGroupsView.syncStates(state, getAnswer(), correction);
    return correction;
  };
  setCorrectionFn && setCorrectionFn(getCorrection);

  useEffect(() => {
    modified && onModified && onModified();
  }, [onModified, modified]);

  return (
    <div
      className={`flex flex-col gap-2 py-4 ${
        !resultPages ? 'rounded-lg bg-[#f8f8f9] px-2' : 'mt-2'
      }`}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      data-state={encode(JSON.stringify(state))}
      onFocus={() => focusWidget(state?.uid)}
    >
      <div className="px-2">
        {!resultPages && (
          <WidgetHeader
            index={index}
            score={round2(score)}
            maxScore={state.score}
            answered={answered}
            titleWidget={state.titleWidget || t('widgets.type.sort_groups')}
          />
        )}
      </div>
      <div className="px-2">
        <FroalaTextareaView value={state.vraag} />
      </div>

      <div>
        <GroupedItemsLearnerWidget
          state={state}
          value={state?.groups}
          answer={answer?.groups}
          answered={answered}
          onChange={(next, items) => {
            setMyGroups(next);
            setBankItems(items);
          }}
        />
        <div className="px-2">
          {(state.immediateFeedback || correctionType === 'autofeedback') &&
            courseStatus !== 'disabled' && (
              <PartialSubmitSection
                setSubmitted={setSubmitted}
                setModified={setModified}
                answered={answered}
                onSave={onSave.bind(null, false, false, state)}
              />
            )}
          {(resultPages || showAnswers || answered || submitted) &&
          state?.antwoord ? (
            <ExplanationSection state={state} />
          ) : null}
        </div>
      </div>
    </div>
  );
};

SortGroupsView.syncStates = (state, answer, correction) => {
  if (isEmpty(answer)) {
    answer.uid = state.uid;
    answer.groups = state?.groups?.map((x) => ({ ...x, items: [] }));
  }
  if (isEmpty(correction) || answer?.groups?.length !== state?.groups?.length) {
    correction.uid = state.uid;
    if (correction.score === undefined || correction.score === null) {
      const correctMap = {};
      const studentMap = {};

      state?.groups?.forEach((group) => {
        (group.items || []).forEach((item) => {
          correctMap[item.uid] = group.uid;
        });
      });

      answer?.groups?.forEach((group) => {
        (group.items || []).forEach((item) => {
          studentMap[item.uid] = group.uid;
        });
      });

      const total = Object.keys(correctMap).length;

      const correct = Object.keys(correctMap).filter(
        (itemUid) => studentMap[itemUid] === correctMap[itemUid]
      ).length;

      const rawScore = total > 0 ? (correct / total) * state.score : 0;
      correction.score = rawScore;
    }
  }
};
